import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/articles';
import useSEO, { SITE_URL } from '@/hooks/useSEO';

export default function AboutPage() {
  const { t, i18n } = useTranslation('common');
  const currentLang = i18n.language || 'en';

  useSEO({
    title: t('metaAboutTitle'),
    description: t('metaAboutDesc'),
    canonical: `${SITE_URL}/${currentLang}/about`,
    lang: currentLang,
    ogType: 'website',
    ogTitle: t('metaAboutTitle'),
    ogDesc: t('metaAboutDesc'),
    ogUrl: `${SITE_URL}/${currentLang}/about`,
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: 'About AllChinaBuy Spreadsheet',
      url: `${SITE_URL}/${currentLang}/about`,
      description: t('metaAboutDesc'),
    },
  });

  useEffect(() => {
    document.documentElement.lang = currentLang;
  }, [currentLang]);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-20">
        <nav aria-label="Breadcrumb" className="border-b border-background-200">
          <div className="container-main py-3">
            <ol className="flex items-center gap-2 text-sm">
              <li>
                <Link to={`/${currentLang}`} className="text-foreground-500 hover:text-primary-600 transition-colors">
                  {t('breadcrumbHome')}
                </Link>
              </li>
              <li><i className="ri-arrow-right-s-line text-foreground-300 text-xs" /></li>
              <li className="text-foreground-700 font-medium">{t('breadcrumbAbout')}</li>
            </ol>
          </div>
        </nav>

        <section className="section-padding">
          <div className="container-main max-w-3xl mx-auto">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-6">
              {t('aboutTitle')}
            </h1>

            <div className="article-content">
              <p>
                Welcome to <strong>AllChinaBuy Spreadsheet</strong>, an independent resource hub designed to help buyers research, organize, and discover product information across a wide range of categories. Our mission is simple: make product discovery and purchasing research easier by bringing useful resources together in one searchable, well-organized hub.
              </p>

              <h2>What We Do</h2>
              <p>
                We curate and organize information about AllChinaBuy products, sellers, shipping options, quality control processes, and buying guides. Our platform serves as a centralized knowledge base where you can explore product categories, compare shipping methods, find current coupon codes, and learn about safe purchasing practices.
              </p>

              <h2>Our Approach</h2>
              <p>
                We believe in transparency and practical information. All content on this site is researched and updated regularly to provide the most current insights. We do not fabricate reviews, ratings, or statistics. Instead, we focus on educating buyers with neutral, factual information that helps you make your own informed decisions.
              </p>

              <h2>Categories We Cover</h2>
              <p>
                Our resource hub spans major product categories including shoes, hoodies and sweaters, t-shirts, jackets, pants and shorts, headwear, sets, underwear, jerseys, accessories, bags, belts, perfumes, and more. Each category connects to detailed product listings and research tools on our main website.
              </p>

              <h2>Editorial Policy</h2>
              <p>
                We maintain strict editorial independence. We do not accept payment for product placement or favorable reviews. Our content is created based on research, user feedback, and publicly available information. We clearly label sponsored content when applicable and always disclose our relationship with partners.
              </p>

              <h2>Content Updates</h2>
              <p>
                Information in the buying and shipping space changes rapidly. We update our guides, comparison tables, and resource pages regularly. Each article displays its last updated date so you can judge the freshness of the information. We encourage users to verify current pricing and availability directly on the main AllChinaBuy platform before making purchases.
              </p>

              <h2>Contact Us</h2>
              <p>
                Have suggestions for new content or resources? Want to report outdated information? We welcome feedback from our community. Visit our <Link to={`/${currentLang}/contact`}>Contact page</Link> to get in touch with our editorial team.
              </p>
            </div>

            <div className="mt-10 p-6 rounded-lg bg-primary-50 border border-primary-200">
              <p className="text-sm text-foreground-700 mb-4">
                Continue exploring our <Link to={`/${currentLang}`} className="text-primary-600 hover:text-primary-700 underline">homepage resources</Link> or browse the latest <Link to={`/${currentLang}/blog`} className="text-primary-600 hover:text-primary-700 underline">buying guides</Link>.
              </p>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="btn-primary gap-2 text-sm"
              >
                <i className="ri-external-link-line" />
                {t('visitMain')}
              </a>
            </div>
          </div>
        </section>

        <section className="section-padding bg-background-100">
          <div className="container-main">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
              {t('latestResources')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {articles.slice(0, 3).map((article) => (
                <article key={article.slug} className="card card-hover overflow-hidden">
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={article.image}
                      alt={t(article.key)}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-5">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800 mb-2">
                      {article.category}
                    </span>
                    <h3 className="font-heading font-semibold text-sm text-foreground-900 mb-2 line-clamp-2">
                      {t(article.key)}
                    </h3>
                    <Link
                      to={`/${currentLang}/blog/${article.slug}`}
                      className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700"
                    >
                      {t('readMore')}
                      <i className="ri-arrow-right-line" />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}